import { ChangeDetectionStrategy, Component, computed, inject, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { MultiCheckboxSelectComponent } from '@shared/components/multi-checkbox-select-dropdown/multi-checkbox-select-dropdown.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { GqlService } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { MainQuery } from '@shared/store/main/main.query';
import { TrialUserQuery } from '@shared/store/trial-users/trial-user.query';
import { TrialsQuery } from '@shared/store/trials/trials.query';

export interface CopyUsersModalData {
  containerClass: string[];
}

export interface CopyUsersModalResult {
  aux_user_ids: string[];
  copy_permissions: boolean;
  trial_ids: string[];
}

export const injectCopyUsersModal =
  (overlayService = inject(OverlayService), gqlService = inject(GqlService)) =>
  async () => {
    const ref = overlayService.openPopup<
      CopyUsersModalData,
      CopyUsersModalResult,
      CopyUsersModalComponent
    >({
      content: CopyUsersModalComponent,
      data: {
        containerClass: ['overflow-visible'],
      },
      settings: {
        header: 'Copy Users',
        preventAutoCloseModal: true,
        primaryButton: {
          action: (instance) => instance?.onSave(),
        },
      },
    });

    const { data: refData } = await firstValueFrom(ref.afterClosed$);

    if (refData) {
      const { aux_user_ids, copy_permissions, trial_ids } = refData;
      const {
        data: response,
        errors,
        success,
      } = await firstValueFrom(
        gqlService.copyUsersToTrials$({
          aux_user_ids,
          copy_permissions,
          trial_ids,
        })
      );

      if (success && response) {
        overlayService.success('Users copied successfully');

        return true;
      }

      overlayService.error(errors?.[0] || 'Failed to copy users');
    }

    return false;
  };

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgSelectModule, FormsModule, CheckboxComponent, MultiCheckboxSelectComponent],
  styles: [
    `
      :host {
        display: block;
        width: 530px;
      }
    `,
  ],
  template: `
    <div class="space-y-[25px]">
      <div class="flex items-center">Select user(s) to copy to other trial(s)</div>

      <div>
        <div class="mb-[4px]">Users</div>
        <aux-multi-checkbox-select
          [options]="userOptions()"
          placeholder="Select users"
          [(selectedItems)]="selectedUserIDs"
        />
      </div>

      <div>
        <div class="mb-[4px]">Trial(s)</div>
        <aux-multi-checkbox-select
          [options]="trialOptions()"
          placeholder="Select trials"
          [(selectedItems)]="selectedTrialIDs"
        />
      </div>

      <div class="relative">
        <aux-checkbox [(ngModel)]="copyPermissions">
          <div>Copy All Permissions for Selected Users</div>
        </aux-checkbox>
        @if (copyPermissions()) {
          <div class="ml-[24px]">
            If a user already exists in a trial, permissions will be overridden.
          </div>
        }
      </div>

      @if (showError() && errorMessage()) {
        <div class="rounded-md bg-aux-error p-4 text-white">{{ errorMessage() }}</div>
      }
    </div>
  `,
})
export class CopyUsersModalComponent {
  authQuery = inject(AuthQuery);
  mainQuery = inject(MainQuery);
  ref = inject(CustomOverlayRef<CopyUsersModalResult, CopyUsersModalData>);
  trialsQuery = inject(TrialsQuery);
  userQuery = inject(TrialUserQuery);

  showError = signal(false);

  errorMessage = computed(() => {
    if (this.selectedUserIDs().length === 0) {
      return 'Please select at least one user';
    }

    if (this.selectedTrialIDs().length === 0) {
      return 'Please select at least one trial';
    }

    return '';
  });
  trialOptions = computed(() => {
    const trials = this.trialsQuery.trials();
    const trialKey = this.mainQuery.trialKey();

    return trials
      .filter((trial) => trial.id !== trialKey)
      .map((trial) => ({
        label: trial.short_name || '',
        value: trial.id,
      }));
  });
  userOptions = computed(() => {
    const users = this.userQuery.users();
    const userSub = this.authQuery.user().sub;

    return users
      .filter((user) => user.sub !== userSub)
      .map((user) => ({
        label: `${user.given_name} ${user.family_name}`,
        value: user.sub,
      }));
  });

  copyPermissions = model<boolean>(true);
  selectedTrialIDs = model<string[]>([]);
  selectedUserIDs = model<string[]>([]);

  onSave() {
    this.showError.set(true);

    if (this.errorMessage()) {
      return;
    }

    this.ref.close({
      aux_user_ids: this.selectedUserIDs(),
      copy_permissions: this.copyPermissions(),
      trial_ids: this.selectedTrialIDs(),
    });
  }
}
